/**
 * OutputSeriesChart (#2341, #2373) — the cumulative Plan / Prognose / Ist
 * multi-line chart. #2373 pins the x-axis to a fixed Juli–Juli Wirtschaftsjahr
 * window (computed ticks + ``interval={0}`` + a "Heute" ``ReferenceLine``,
 * mirroring the Liquiditätsplanung BalanceChart) so empty months stay visible
 * and the window ends at Jun, not at the last pick; it also adds toggleable
 * thin/muted −1/−2/−3-Jahr prior-year Ist overlays with a top-right legend.
 * Production-agnostic: it renders whatever ``OutputChartRow[]`` the pure
 * ``buildOutputChart`` helper produced — recharts does not render under jsdom,
 * so this stays a thin declarative view.
 *
 * #2456: the ephemeral hover ``<Tooltip>`` is gone; clicking a point selects
 * that bucket (recharts ``onClick`` → ``activeLabel``, precise via recharts' own
 * plot geometry — the deferred pan pointer-capture of #2457 keeps the click
 * reaching recharts) and the selection is marked on the plot (a violet
 * ``ReferenceLine`` + a per-series dot). The selected day's numbers render in the
 * page's "Ausgewählter Tag" readout below the chart.
 *
 * #2506: the chart used to label the same series TWICE — an interactive
 * prior-year checkbox stack tucked top-right, crowding the zoom toolbar in the
 * same corner, plus a passive recharts ``<Legend />`` at the bottom that only
 * echoed the enabled overlays and did nothing on click. There is now ONE legend:
 * a single slim strip sharing a row with the toolbar, built from the canonical
 * ``outputChartSeries`` list that also drives the ``<Line>``s and the selection
 * dots, so a label, colour or stroke cannot drift between plot and legend. The
 * prior-year entries are ``aria-pressed`` toggle chips (the ``YearAxisControl``
 * pattern) and each drawn series carries its end value as a KPI.
 *
 * #2544-A: the Zeitraster segmented control (now ``1T | 1W | 1M | 1J``, with the
 * week bucket new) moved OFF the page header and into this toolbar row, beside
 * the zoom controls — chart granularity belongs to the chart. It is deliberately
 * the only such control on the page: the page header no longer renders a second
 * one, so ``?g=`` has exactly one widget driving it.
 *
 * #2544-B: a second toolbar control, ``Kumuliert | Einzelernte``, switches the
 * DATA mode. Kumuliert is the running season total (the original chart, pixel
 * unchanged as of that PR — see #2594 below, which deliberately ends that);
 * Einzelernte plots the amount harvested in each bucket, with Ist as columns.
 * The plot host is therefore a ``ComposedChart`` — it must be able to carry a
 * ``<Bar>`` and the ``<Line>``s at once. Which series is a bar is read off the
 * canonical ``outputChartSeries`` list (``mark``), never decided here, so legend
 * and plot cannot drift.
 *
 * #2546-B: a third toolbar control, ``Linie | Säulen``, exposes the PRESENTATION
 * axis #2544-B left as an internal default. It is orthogonal to the data mode —
 * all four combinations are valid — so the component takes an already-RESOLVED
 * ``presentation`` and forwards it to ``buildChartSeries``. The tri-state
 * resolution of ``?pm=`` against the mode lives in ``resolvePresentation``; this
 * view never re-derives it, which is what keeps the toggle a pure override of a
 * default rather than a second place deciding whether Ist is a column.
 *
 * #2594: the solitary-bucket dot (see ``isSolitaryInView``) is no longer gated to
 * Einzelernte. Kumuliert therefore gains a visible dot exactly where a one-bucket
 * series previously drew nothing at all — a DELIBERATE break with #2544-B's
 * PR-scoped "pixel unchanged" promise, not a regression.
 */
import { useId } from 'react';
import { ChartColumn, ChartLine } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { bucketKey, type ChartMode, type Grain, type OutputChartRow, withForecastJoin } from './outputSeries';
import {
  VALUE_KIND_LABEL,
  buildChartSeries,
  plotOrder,
  seriesEndValue,
  type ChartPresentation,
  type ChartSeries,
  type PriorYearToggle,
} from './outputChartSeries';
import { todayIso } from '../../../utils/today';
import {
  ChartPresentationToggle,
  ChartZoomControls,
  sliceRows,
  useChartTimeWindow,
  yearBoundaryLines,
  type ChartPresentationOption,
} from '../../../components/charts';

const SELECTED_COLOR = '#7c3aed'; // violet — the click-selected bucket marker (distinct from grey "Heute")

// #2544-A — the chart-level granularity control. Short stock-app style labels
// (``1T`` … ``1J``) keep the toolbar compact next to the zoom buttons; the German
// word is the ``title`` tooltip, so the accessible NAME stays exactly the visible
// text (WCAG 2.5.3 "Label in Name") while the abbreviation is still explained.
const GRAINS: Grain[] = ['day', 'week', 'month', 'year'];
const GRAIN_LABELS: Record<Grain, string> = { day: '1T', week: '1W', month: '1M', year: '1J' };
const GRAIN_TITLES: Record<Grain, string> = { day: 'Tag', week: 'Woche', month: 'Monat', year: 'Jahr' };

// #2544-B — the data-mode control. Full German words rather than the Zeitraster's
// abbreviations: "Kumuliert vs Einzelernte" is a semantic choice about what the
// numbers MEAN, and abbreviating it would leave the farmer guessing.
const MODES: ChartMode[] = ['cumulative', 'perPeriod'];
const MODE_LABELS: Record<ChartMode, string> = { cumulative: 'Kumuliert', perPeriod: 'Einzelernte' };
const MODE_TITLES: Record<ChartMode, string> = {
  cumulative: 'Aufsummierter Saisonverlauf',
  perPeriod: 'Erntemenge je Zeitraum',
};

/** The Ist columns' fill opacity: enough body to read as an amount, translucent
 *  enough that the thin prior-year lines behind them stay legible. */
const BAR_FILL_OPACITY = 0.85;

// #2546-B — the presentation control. Icon-only (the issue asks for a compact
// toggle with no text labels), so the German word is the accessible NAME via
// ``aria-label`` and the tooltip, not visible text. Linie first: it is the
// chart's historical look, and the reading order then runs "the familiar one,
// then the new one".
const PRESENTATIONS: readonly ChartPresentationOption<ChartPresentation>[] = [
  { value: 'linie', icon: <ChartLine size={16} aria-hidden="true" />, label: 'Linie' },
  { value: 'saeulen', icon: <ChartColumn size={16} aria-hidden="true" />, label: 'Säulen' },
];

// #2506 — every series' label/colour/stroke now lives in ``outputChartSeries``,
// so the legend and the plot can no longer disagree. Re-exported here because
// ErnteprognosePage + selectedDayReadout already import the type from this module.
export type { PriorYearToggle };

export interface OutputSeriesChartProps {
  rows: OutputChartRow[];
  unit: string;
  grain: Grain;
  /** Pick a new rollup grain (``?g=``) — the toolbar's Zeitraster control (#2544-A). */
  onGrainChange: (grain: Grain) => void;
  /** Which numbers to plot (``?m=``) — the toolbar's Datenansicht control (#2544-B). */
  mode: ChartMode;
  onModeChange: (mode: ChartMode) => void;
  /**
   * How to draw them (#2546-B) — the toolbar's Darstellung control. Already
   * RESOLVED against ``mode`` by the page (``resolvePresentation``): this view
   * takes the answer, never the raw ``?pm=`` param, so the tri-state rule lives
   * in exactly one place.
   */
  presentation: ChartPresentation;
  onPresentationChange: (presentation: ChartPresentation) => void;
  showHorizon: boolean;
  horizonDays: number;
  /** True when a plan target exists — otherwise the Plan line is omitted (greyed away). */
  hasPlan: boolean;
  /** Available prior-year overlays (offsets that actually have prior-year data). */
  priorYears: PriorYearToggle[];
  onTogglePriorYear: (offset: number) => void;
  /** The click-selected bucket key (``?d=``), or null. Marked on the plot. */
  selectedBucket?: string | null;
  /** Persist a click as the selected bucket (#2456 Ask 4). */
  onSelectBucket?: (bucket: string) => void;
}

export default function OutputSeriesChart({
  rows,
  unit,
  grain,
  onGrainChange,
  mode,
  onModeChange,
  presentation,
  onPresentationChange,
  showHorizon,
  horizonDays,
  hasPlan,
  priorYears,
  onTogglePriorYear,
  selectedBucket = null,
  onSelectBucket,
}: OutputSeriesChartProps) {
  // #2436 — stock-app zoom/pan as a view transform over the Heute-centred axis.
  // Default fit shows the whole −1y…+1y span (so "Heute" sits centred); zoom/pan
  // refine it, reset returns to the full window. Windowed row-slice keeps the
  // ~2-year daily spine smooth. Runs on the REAL (un-joined) rows so the ticks,
  // selection marker, and readout all read true data.
  const zoom = useChartTimeWindow(rows, { ariaLabel: 'Ertragsdiagramm (zoom- und verschiebbar)' });
  // Unique id prefix so each legend entry's value can be the accessible
  // DESCRIPTION of its toggle without polluting the toggle's accessible NAME
  // (which stays exactly the year label).
  const uid = useId();

  // #2544-B — the data-mode control, and #2544-A's ONE Zeitraster control (the
  // page header no longer carries a copy). Both are rendered above the early
  // return too: with no rows there is nothing to zoom, but neither re-graining
  // nor switching what the numbers mean may become a dead end.
  const modeControl = (
    <div className="ernteprognose-chart__mode" role="group" aria-label="Datenansicht">
      {MODES.map((m) => (
        <button
          key={m}
          type="button"
          className="ernteprognose-chart__mode-segment"
          aria-pressed={mode === m}
          title={MODE_TITLES[m]}
          onClick={() => onModeChange(m)}
        >
          {MODE_LABELS[m]}
        </button>
      ))}
    </div>
  );

  const grainControl = (
    <div className="ernteprognose-chart__grain" role="group" aria-label="Zeitraster">
      {GRAINS.map((g) => (
        <button
          key={g}
          type="button"
          className="ernteprognose-chart__grain-segment"
          aria-pressed={grain === g}
          title={GRAIN_TITLES[g]}
          onClick={() => onGrainChange(g)}
        >
          {GRAIN_LABELS[g]}
        </button>
      ))}
    </div>
  );

  // #2546-B — the presentation control, last in the segmented cluster and
  // immediately left of the zoom buttons: it is the other icon-only control on
  // the row, so the two read as one block rather than an icon marooned between
  // two word toggles. Like them it renders above the early return — with no rows
  // there is nothing to draw, but switching how the chart WOULD be drawn must
  // not become a dead end either.
  const presentationControl = (
    <ChartPresentationToggle
      value={presentation}
      options={PRESENTATIONS}
      onChange={onPresentationChange}
      ariaLabel="Darstellung"
    />
  );

  if (rows.length === 0) {
    return (
      <div className="ernteprognose-chart">
        <div className="ernteprognose-chart__legendbar">
          <div className="ernteprognose-chart__toolbar">
            {modeControl}
            {grainControl}
            {presentationControl}
          </div>
        </div>
        <p className="ernteprognose-chart__empty" role="status">
          Noch keine Prognose- oder Erntedaten für diese Kultur.
        </p>
      </div>
    );
  }

  const visibleRows = zoom.visibleRows;
  // #2455 Bug 2 — the seeded Prognose run continues from the Ist cumulative; this
  // bridges the dashed line back to the last Ist point so the two lines meet at
  // Heute (``connectNulls={false}`` needs a real point on every bucket from the
  // boundary to the forecast start). Join the FULL rows BEFORE slicing to the zoom
  // window, then slice: joining after the slice would lose the bridge whenever the
  // last Ist bucket is scrolled just off-plot while the gap/forecast start is
  // still visible. LINE-ONLY: ``visibleRows`` (the real, un-joined data) still
  // drives the selection dots below AND the page's "Ausgewählter Tag" readout, so
  // the synthetic bridge never shows a phantom Prognose value in the readout or a
  // comparison in the Ampel verdict.
  //
  // #2544-B — CUMULATIVE-MODE ONLY. The bridge is a synthetic flat ``forecastCum``
  // whose whole purpose is to make two cumulative CURVES meet; in Einzelernte the
  // plotted fields are per-bucket amounts, where a carried-forward total would be
  // a value nobody forecast. Einzelernte plots the real rows.
  const plotData = sliceRows(mode === 'cumulative' ? withForecastJoin(rows) : rows, zoom.window);
  const fmt = (v: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(v);

  // #2506 — ONE series list drives the legend, the ``<Line>``s and the selection
  // dots. ``series`` is reading order (what the legend renders, matching the
  // "Ausgewählter Tag" readout); ``drawnSeries`` is recharts source order, which is
  // z-order, so the prior-year overlays sit under the primary lines.
  const series = buildChartSeries({ showHorizon, horizonDays, hasPlan, priorYears, mode, presentation });
  const drawnSeries = plotOrder(series);

  /**
   * #2544-B, widened by #2594 — a ``<Line dot={false} connectNulls={false}>``
   * whose non-null run is a SINGLE point strokes an empty path: recharts needs
   * two points to draw a segment (the same trap ``withForecastJoin``'s docstring
   * calls out). Such a series silently VANISHES while its legend entry still
   * reports a KPI, claiming it was drawn. ``connectNulls`` does not exempt
   * anyone — Plan sets it ``true`` (it bridges its own interior gaps), but one
   * point is one point, so the guard covers every ``<Line>`` alike.
   *
   * MODE-AGNOSTIC, because both modes reach it. Einzelernte plots the real rows,
   * so a series confined to one bucket — a forecast inside a single month at
   * that grain — is invisible outright. Kumuliert is not immune either:
   * ``withForecastJoin`` only ever extends ``forecastCum``, only when actuals
   * precede the forecast, and it bails when the forecast's first bucket already
   * carries an Ist, so Prognose can be solitary there too; and at 1J grain a
   * season inside one calendar year leaves ``actualCum`` with a single non-null
   * across the whole ``['2025','2026','2027']`` axis, which is the routine case
   * (#2594 verified it live on WF). Either mode can also be narrowed to one
   * visible point by the zoom window, since ``plotData`` is already sliced.
   *
   * #2544-B scoped this to ``perPeriod`` purely to honour that PR's "Kumuliert
   * byte-identical" AC; #2594 dropped the gate, so Kumuliert now shows a dot
   * where it previously showed nothing at all. That is the intended, visible
   * change — not a regression against the v31.4.0 screenshots.
   *
   * Dot exactly those series. Per-bucket values are non-null across the whole of
   * a series' own ``[first .. last]`` range (gaps inside it are a real 0), so a
   * run is always contiguous: "exactly one non-null in view" IS the invisible
   * case, and every other line keeps ``dot={false}`` unchanged. A series drawn
   * as a ``<Bar>`` (#2546's Säulen) never reaches here — a column of one is
   * perfectly visible — so there is no double-marking.
   */
  const isSolitaryInView = (s: ChartSeries): boolean => {
    let seen = 0;
    for (const r of plotData) {
      if (r[s.dataKey] != null && ++seen > 1) return false;
    }
    return seen === 1;
  };
  const priorSeries = series.filter((s) => s.offset !== null);
  const primarySeries = series.filter((s) => s.offset === null);

  // Computed ticks — the first bucket of each month across the fixed window,
  // thinned to ~12 so labels never collide (BalanceChart pattern). At month
  // grain every bucket is its own month; at year grain there are ≤2 buckets.
  const monthFirsts: string[] = [];
  const seenMonths = new Set<string>();
  for (const r of visibleRows) {
    const ym = r.bucket.slice(0, 7);
    if (!seenMonths.has(ym)) {
      seenMonths.add(ym);
      monthFirsts.push(r.bucket);
    }
  }
  const tickStep = Math.max(1, Math.ceil(monthFirsts.length / 12));
  const ticks = monthFirsts.filter((_, i) => i % tickStep === 0 || i === monthFirsts.length - 1);

  const axisTick = (bucket: string): string => {
    if (grain === 'year') return bucket;
    const [y, m] = bucket.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('de-DE', {
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    });
  };

  const todayBucket = bucketKey(todayIso(), grain);

  // #2456 Ask 4 — the click-selected bucket, marked on the plot ONLY while it is
  // inside the current zoom window (a selection scrolled off-plot keeps its ?d=
  // + readout, but drops the on-plot marker). A dot per VISIBLE series at its
  // value there, colour-matched to the line.
  const selectedRow = selectedBucket ? visibleRows.find((r) => r.bucket === selectedBucket) : undefined;
  // #2506 — derived from the SAME series list as the lines and the legend (it used
  // to hand-roll a third copy of the series→colour mapping). Prior-year dots come
  // first, so the primary-series dots land on top — matching the line z-order.
  const selectedDots = selectedRow
    ? drawnSeries.flatMap((s) => {
        const value = selectedRow[s.dataKey];
        return value == null ? [] : [{ key: s.key, value, color: s.color }];
      })
    : [];

  /**
   * One legend entry. Prior-year series are ``aria-pressed`` toggle buttons
   * (the ``YearAxisControl`` chip pattern); the primary series are static. The
   * end value sits OUTSIDE the button and is wired in via ``aria-describedby``,
   * so the toggle's accessible NAME stays exactly the year label while a screen
   * reader still announces the number and what it means.
   */
  const legendEntry = (s: ChartSeries) => {
    const value = s.enabled ? seriesEndValue(rows, s) : null;
    const valueId = `${uid}-${s.key}`;
    const offset = s.offset;
    const swatch = (
      <span
        className="ernteprognose-chart__legend-swatch"
        aria-hidden="true"
        style={{
          ['--series-color' as string]: s.color,
          ['--series-stroke' as string]: s.strokeDasharray ? 'dashed' : 'solid',
        }}
      />
    );
    const face = (
      <>
        {swatch}
        <span className="ernteprognose-chart__legend-label">{s.label}</span>
      </>
    );
    return (
      <span
        key={s.key}
        className="ernteprognose-chart__legend-entry"
        data-testid={`legend-entry-${s.key}`}
        data-label={s.label}
      >
        {offset !== null ? (
          <button
            type="button"
            className="ernteprognose-chart__legend-toggle"
            aria-pressed={s.enabled}
            aria-describedby={s.enabled ? valueId : undefined}
            onClick={() => onTogglePriorYear(offset)}
          >
            {face}
          </button>
        ) : (
          <span className="ernteprognose-chart__legend-static">{face}</span>
        )}
        {s.enabled && (
          <span className="ernteprognose-chart__legend-value" id={valueId}>
            {/* #2544-B — in Kumuliert this number is self-evidently the line's
                right-hand endpoint, so the label stays screen-reader-only and
                the strip stays slim. In Einzelernte the plot shows per-bucket
                amounts while the KPI stays the season total, so the number has
                no visual anchor at all ("Ist 480 kg" beside a 380 kg tallest
                column) — surface what it means exactly there. */}
            <span
              className={`ernteprognose-chart__legend-kind${
                mode === 'perPeriod' ? ' ernteprognose-chart__legend-kind--visible' : ''
              }`}
            >
              {VALUE_KIND_LABEL[s.valueKind]}{' '}
            </span>
            {value == null ? '—' : `${fmt(value)} ${unit}`}
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="ernteprognose-chart">
      {/* #2506 — the ONE legend, on a shared row with the zoom toolbar. The two
          used to be separate right-aligned stacks in the same corner, which read
          as one crowded control cluster and cost the plot two text rows. */}
      <div className="ernteprognose-chart__legendbar">
        <div className="ernteprognose-chart__legend" role="group" aria-label="Diagrammlegende">
          {primarySeries.map(legendEntry)}
          {priorSeries.length > 0 && (
            <div className="ernteprognose-chart__overlays" role="group" aria-label="Vorjahresvergleich">
              {priorSeries.map(legendEntry)}
            </div>
          )}
        </div>
        <div className="ernteprognose-chart__toolbar">
          {modeControl}
          {grainControl}
          {presentationControl}
          <div className="ernteprognose-chart__zoom">
            <ChartZoomControls
              canZoomIn={zoom.canZoomIn}
              canZoomOut={zoom.canZoomOut}
              canPanPast={zoom.canPanPast}
              canPanFuture={zoom.canPanFuture}
              isDefault={zoom.isDefault}
              onZoomIn={zoom.zoomIn}
              onZoomOut={zoom.zoomOut}
              onPanPast={zoom.panPast}
              onPanFuture={zoom.panFuture}
              onReset={zoom.reset}
            />
          </div>
        </div>
      </div>
      <div className="ernteprognose-chart__plot" {...zoom.containerProps}>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart
            data={plotData}
            margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
            onClick={(state) => {
              // #2456 Ask 4 — recharts hands the clicked bucket key back as
              // ``activeLabel`` (computed through its own plot geometry, so it is
              // pixel-accurate where the container-relative hook primitive is
              // not). A plain click reaches recharts because #2457 defers the pan
              // pointer-capture until a real drag.
              const label = state?.activeLabel;
              if (onSelectBucket && typeof label === 'string') onSelectBucket(label);
            }}
          >
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis
            dataKey="bucket"
            tick={{ fontSize: 12 }}
            ticks={ticks}
            interval={0}
            tickFormatter={axisTick}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            width={64}
            tickFormatter={fmt}
            label={{ value: unit, angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
          />
          {/* #2506 — no ``<Legend />``: it was a passive, non-interactive echo of
              the toggle block above the plot. The single legend is that strip. */}
          {/* #2501 — 01.01 splitters before "Heute" and the selection marker, so
              both stay on top of a near-coincident boundary (source z-order).
              Anchored on the REAL (un-joined) visible buckets, which are the same
              bucket set `plotData` renders. */}
          {yearBoundaryLines(visibleRows.map((r) => r.bucket))}
          <ReferenceLine
            x={todayBucket}
            stroke="#9ca3af"
            strokeDasharray="2 3"
            label={{ value: 'Heute', position: 'insideTopRight', fontSize: 11, fill: '#6b7280' }}
          />
          {/* #2456 Ask 4 — the click-selected bucket: a solid violet vertical,
              distinct from the grey dashed "Heute". Rendered only while inside
              the current zoom window (``selectedRow`` derives from visibleRows). */}
          {selectedRow && (
            <ReferenceLine x={selectedRow.bucket} stroke={SELECTED_COLOR} strokeWidth={1.5} />
          )}
          {/* #2506 — one mark per DRAWN series, straight off the canonical list:
              prior-year overlays first so they sit under the primary lines
              (source order = z-order), and every ``name`` is the exact string its
              legend entry renders.
              #2544-B — the mark TYPE is read off the list too (``s.mark``), never
              re-derived from the mode here: that is what keeps #2546's
              ``Linie | Säulen`` toggle a pure override of a default rather than a
              second place deciding whether Ist is a column. */}
          {drawnSeries.map((s) =>
            s.mark === 'bar' ? (
              <Bar
                key={s.key}
                dataKey={s.dataKey}
                name={s.label}
                fill={s.color}
                fillOpacity={BAR_FILL_OPACITY}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.dataKey}
                name={s.label}
                stroke={s.color}
                strokeDasharray={s.strokeDasharray}
                // #2594 — mode-agnostic: a one-point run is invisible in
                // Kumuliert exactly as in Einzelernte. See `isSolitaryInView`.
                dot={isSolitaryInView(s) ? { r: 2.5, fill: s.color } : false}
                strokeWidth={s.strokeWidth}
                connectNulls={s.connectNulls}
                isAnimationActive={false}
              />
            ),
          )}
          {/* Selection dots on top of the lines (source order = z-order). */}
          {selectedRow &&
            selectedDots.map((d) => (
              <ReferenceDot
                key={`sel-${d.key}`}
                x={selectedRow.bucket}
                y={d.value}
                r={4}
                fill={d.color}
                stroke="#ffffff"
                strokeWidth={1.5}
                isFront
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
